import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import { SITE, IMAGES, waLink } from "@/config/site";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const visualRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: visualRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="home" className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-15%] h-[560px] w-[560px] rounded-full bg-[#0050e0]/[0.05] blur-3xl"
      />
      <div className="container-x grid items-center gap-12 py-14 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:py-24">
        <div className="lg:col-span-7">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          >
            Parafarmacia • Dermocosmesi • Benessere
          </motion.p>

          <h1 className="h-display mt-6 text-4xl leading-[1.06] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.3}>Prendersi cura di sé,</MaskedLine>
            <MaskedLine delay={0.42}>
              con il <span className="text-[#0050e0]">consiglio giusto.</span>
            </MaskedLine>
          </h1>

          <motion.p
            className="mt-6 max-w-xl text-base leading-relaxed text-[#575a61] sm:text-lg"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          >
            Dermocosmesi, benessere e consulenza professionale in uno spazio pensato per prenderti cura di te.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          >
            <a href="#chi-siamo" data-testid="hero-cta-discover" className="btn-primary">
              Scopri Farma Store
              <ArrowDown className="h-4 w-4" />
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="btn-wa"
            >
              <WhatsAppIcon className="h-5 w-5 text-[#1faf57]" />
              Scrivici su WhatsApp
            </a>
          </motion.div>

          <motion.p
            data-testid="hero-address-badge"
            className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#575a61]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <MapPin className="h-4 w-4 text-[#0050e0]" />
            {SITE.addressShort}
          </motion.p>
        </div>

        <motion.div
          className="relative lg:col-span-5"
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
        >
          <div ref={visualRef} className="relative aspect-[4/3] overflow-hidden rounded-[2rem] sm:aspect-[4/5] lg:aspect-[4/5]">
            <motion.img
              src={IMAGES.hero.src}
              alt={IMAGES.hero.alt}
              style={{ y: imgY }}
              className="absolute inset-0 h-[112%] w-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
            <div aria-hidden="true" className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-black/10" />
          </div>

          <motion.div
            data-testid="hero-floating-card"
            className="absolute -bottom-5 left-4 flex items-center gap-3 rounded-2xl border border-black/[0.06] bg-white/90 p-4 shadow-[0_24px_48px_-24px_rgba(28,29,31,0.3)] backdrop-blur-md sm:left-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
          >
            <img src="/logo-mark.png" alt="" className="h-9 w-9 object-contain" width="512" height="512" />
            <div>
              <p className="font-display text-sm font-bold text-[#1c1d1f]">Parafarmacia e Servizi</p>
              <p className="text-xs text-[#888c95]">{SITE.addressShort} (FR)</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
